import { API_BASE_URL } from "../config/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { appendAuthToken } from "../utils/auth";
import VoucherSimpleManager from "./VoucherSimpleManager";

const SORT_OPTIONS = [
  { value: "voucherDate", label: "Date" },
  { value: "entryType", label: "Complaint / General" },
  { value: "expenseType", label: "Debit Type" },
  { value: "voucherId", label: "Voucher ID" },
  { value: "allowanceAmount", label: "Credit" },
  { value: "amount", label: "Debit" },
  { value: "balance", label: "Balance" }
];

const todayInputValue = () => new Date().toISOString().slice(0, 10);

const formatDisplayDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB");
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2
  });
};

const getExpenseMode = (voucher = {}) =>
  voucher.expenseMode === "complaint" || voucher.entryType === "complaint" ? "complaint" : "general";

const getEntryLabel = (entryType) =>
  getExpenseMode({ entryType, expenseMode: entryType }) === "complaint" ? "Complaint Expense" : "General Expense";

const getVoucherGroupKey = (voucher = {}) =>
  voucher.expenseGroupId || voucher.voucherId || voucher._id;

const sortValue = (voucher, field) => {
  if (field === "voucherDate") return new Date(voucher.date || voucher.voucherDate || voucher.createdAt || 0).getTime();
  if (field === "entryType") return String(voucher.complaintGeneral || getEntryLabel(voucher.expenseMode)).toLowerCase();
  if (field === "expenseType") return String(voucher.expenseSummary || voucher.details?.map((item) => item.title).join(", ") || "").toLowerCase();
  if (field === "voucherId") return String(voucher.expenseGroupId || "").toLowerCase();
  if (field === "allowanceAmount") return Number(voucher.allowanceAmount || 0);
  if (field === "amount") return Number(voucher.expenseAmount || 0);
  if (field === "balance") return Number(voucher.computedBalance || 0);
  return "";
};

function LegacyAdminVouchers() {
  const adminName = localStorage.getItem("adminName") || "Admin";

  // Target toggle: "engineer" or "accounts"
  const [viewTarget, setViewTarget] = useState("engineer");

  // Selections
  const [selectedEngineer, setSelectedEngineer] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");

  // Metadata
  const [engineers, setEngineers] = useState([]);
  const [accountsList, setAccountsList] = useState([]);

  // Data lists
  const [vouchers, setVouchers] = useState([]);
  const [allowances, setAllowances] = useState([]);
  const [balanceInfo, setBalanceInfo] = useState({ openingBalance: 0, currentBalance: 0, lastClearedAt: null });

  // Filters & sorting
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ field: "voucherDate", direction: "desc" });

  // Modals & UI loading
  const [loading, setLoading] = useState(true);
  const [showAllowanceForm, setShowAllowanceForm] = useState(false);
  const [savingAllowance, setSavingAllowance] = useState(false);
  const [showClearDateModal, setShowClearDateModal] = useState(false);
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [clearVoucherDate, setClearVoucherDate] = useState(todayInputValue());
  const [clearDateConfirmationText, setClearDateConfirmationText] = useState("");
  const [clearAllConfirmationText, setClearAllConfirmationText] = useState("");
  const [clearingVoucher, setClearingVoucher] = useState(false);
  const [clearingAllVouchers, setClearingAllVouchers] = useState(false);
  const [vouchersLimit, setVouchersLimit] = useState(20);

  // Credit form state
  const [allowanceForm, setAllowanceForm] = useState({
    location: "",
    amount: "",
    allowanceDate: todayInputValue(),
    notes: ""
  });

  // Load accountant list on mount
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const res = await axios.get(API_BASE_URL + "/api/accounts");
        const rows = Array.isArray(res.data) ? res.data : [];
        setAccountsList(rows);
        if (rows.length > 0) {
          setSelectedAccount(String(rows[0]._id));
        }
      } catch (err) {
        console.error("Failed to load accountant users", err);
      }
    };
    loadAccounts();
  }, []);

  // Selected item hooks
  const selectedEngineerDetails = useMemo(
    () => engineers.find((engineer) => String(engineer._id) === selectedEngineer) || {},
    [engineers, selectedEngineer]
  );

  const selectedAccountDetails = useMemo(
    () => accountsList.find((acc) => String(acc._id) === selectedAccount) || {},
    [accountsList, selectedAccount]
  );

  const fetchPageData = useCallback(async () => {
    try {
      setLoading(true);

      // Always load engineers
      const engineersResult = await axios.get(API_BASE_URL + "/api/engineers");
      const engineerRows = Array.isArray(engineersResult.data) ? engineersResult.data : [];
      setEngineers(engineerRows);
      if (!selectedEngineer && engineerRows.length > 0) {
        setSelectedEngineer(String(engineerRows[0]._id));
      }

      let vouchersResult = { data: [] };
      let allowancesResult = { data: [] };
      let balanceResult = { data: [] };

      if (viewTarget === "engineer") {
        const engId = selectedEngineerDetails.engineerId;
        if (engId) {
          const [v, a, b] = await Promise.all([
            axios.get(API_BASE_URL + "/api/vouchers", { params: { engineer: engId } }),
            axios.get(API_BASE_URL + "/api/vouchers/allowances", { params: { engineer: engId } }),
            axios.get(API_BASE_URL + "/api/vouchers/balance", { params: { engineerId: engId, ownerRole: "engineer" } })
          ]);
          vouchersResult = v;
          allowancesResult = a;
          balanceResult = b;
        }
      } else {
        const accEmail = selectedAccountDetails.email;
        if (accEmail) {
          const accLookup = [selectedAccountDetails.email, selectedAccountDetails.name, selectedAccountDetails._id].filter(Boolean).join(",");
          const [v, a, b] = await Promise.all([
            axios.get(API_BASE_URL + "/api/vouchers", { params: { ownerRole: "accounts", account: accLookup } }),
            axios.get(API_BASE_URL + "/api/vouchers/allowances", { params: { ownerRole: "accounts", account: accLookup } }),
            axios.get(API_BASE_URL + "/api/vouchers/balance", { params: { ownerRole: "accounts", accountId: accEmail } })
          ]);
          vouchersResult = v;
          allowancesResult = a;
          balanceResult = b;
        }
      }

      setVouchers(Array.isArray(vouchersResult.data) ? vouchersResult.data : []);
      setAllowances(Array.isArray(allowancesResult.data) ? allowancesResult.data : []);
      setBalanceInfo(balanceResult?.data?.currentBalance !== undefined ? balanceResult.data : { openingBalance: 0, currentBalance: 0, lastClearedAt: null });
    } catch (error) {
      console.error("Failed to load page details", error);
      setVouchers([]);
      setAllowances([]);
      setBalanceInfo({ openingBalance: 0, currentBalance: 0, lastClearedAt: null });
    } finally {
      setLoading(false);
    }
  }, [viewTarget, selectedEngineer, selectedEngineerDetails.engineerId, selectedAccountDetails.email, selectedAccountDetails.name, selectedAccountDetails._id]);

  useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  const groupedVouchers = useMemo(() => {
    const ordered = [...vouchers].sort((first, second) => {
      const firstDate = new Date(first.voucherDate || first.createdAt || 0).getTime();
      const secondDate = new Date(second.voucherDate || second.createdAt || 0).getTime();
      if (firstDate !== secondDate) return firstDate - secondDate;
      return new Date(first.createdAt || 0).getTime() - new Date(second.createdAt || 0).getTime();
    });

    const groups = new Map();

    ordered.forEach((voucher) => {
      const key = getVoucherGroupKey(voucher);
      const amount = Number(voucher.amount || 0);

      if (!groups.has(key)) {
        groups.set(key, {
          _id: key,
          expenseGroupId: voucher.expenseGroupId || key,
          voucherDate: voucher.voucherDate,
          createdAt: voucher.createdAt,
          expenseMode: getExpenseMode(voucher),
          complaintRefId: voucher.complaintRefId,
          company: voucher.company,
          location: voucher.location,
          rows: [],
          totalAmount: 0
        });
      }

      const group = groups.get(key);
      group.rows.push({
        _id: voucher._id,
        expenseType: voucher.expenseType,
        amount,
        description: voucher.description || ""
      });
      group.totalAmount += amount;
      if (!group.company && voucher.company) group.company = voucher.company;
      if (!group.location && voucher.location) group.location = voucher.location;
      if (!group.complaintRefId && voucher.complaintRefId) group.complaintRefId = voucher.complaintRefId;
    });

    return [...groups.values()].map((group) => ({
      ...group,
      expenseSummary: group.rows.map((row) => row.expenseType).filter(Boolean).join(", ")
    }));
  }, [vouchers]);

  const statementRows = useMemo(() => {
    const expenseRows = groupedVouchers.map((voucher) => ({
      id: `expense-${voucher._id}`,
      _id: `expense-${voucher._id}`,
      kind: "expense",
      date: voucher.voucherDate,
      createdAt: voucher.createdAt,
      complaintGeneral: getEntryLabel(voucher.expenseMode),
      badgeTone: voucher.expenseMode === "complaint" ? "sky" : "emerald",
      refText: [voucher.complaintRefId, voucher.company || voucher.location].filter(Boolean).join(" / ") || "-",
      details: voucher.rows.map((row) => ({
        title: row.expenseType || "-",
        meta: row.description || "",
        amount: Number(row.amount || 0)
      })),
      expenseAmount: Number(voucher.totalAmount || 0),
      allowanceAmount: 0,
      expenseGroupId: voucher.expenseGroupId || "-"
    }));

    const allowanceRows = allowances.map((allowance) => ({
      id: `allowance-${allowance._id}`,
      _id: `allowance-${allowance._id}`,
      kind: "allowance",
      date: allowance.allowanceDate,
      createdAt: allowance.createdAt,
      complaintGeneral: getEntryLabel(allowance.entryType),
      badgeTone: allowance.entryType === "complaint" ? "sky" : "emerald",
      refText: [allowance.complaintRefId, allowance.company || allowance.location].filter(Boolean).join(" / ") || "-",
      details: [{
        title: `Allowance from ${(allowance.givenByRole || "Allowance").toUpperCase()}`,
        meta: [allowance.givenByName, allowance.notes].filter(Boolean).join(" - ") || "Allowance received",
        amount: Number(allowance.amount || 0)
      }],
      expenseAmount: 0,
      allowanceAmount: Number(allowance.amount || 0),
      expenseGroupId: "-"
    }));

    const ordered = [...expenseRows, ...allowanceRows].sort((first, second) => {
      const firstDate = new Date(first.date || first.createdAt || 0).getTime();
      const secondDate = new Date(second.date || second.createdAt || 0).getTime();
      if (firstDate !== secondDate) return firstDate - secondDate;
      return new Date(first.createdAt || 0).getTime() - new Date(second.createdAt || 0).getTime();
    });

    let runningBalance = Number(balanceInfo.openingBalance || 0);

    return ordered.map((row) => {
      runningBalance += Number(row.allowanceAmount || 0) - Number(row.expenseAmount || 0);
      return {
        ...row,
        computedBalance: runningBalance
      };
    });
  }, [allowances, balanceInfo.openingBalance, groupedVouchers]);

  const visibleStatementRows = useMemo(() => {
    if (statementRows.length > 0) return statementRows;
    const hasTarget = viewTarget === "engineer" ? selectedEngineer : selectedAccount;
    if (!hasTarget) return [];
    if (!balanceInfo.lastClearedAt && Number(balanceInfo.currentBalance || 0) === 0) return [];

    return [{
      _id: "opening-balance",
      kind: "opening",
      date: balanceInfo.lastClearedAt || new Date().toISOString(),
      createdAt: balanceInfo.lastClearedAt || new Date().toISOString(),
      complaintGeneral: "Opening Balance",
      badgeTone: "slate",
      refText: "Balance carried forward",
      details: [{
        title: "Opening Balance",
        meta: "Preserved after clearing voucher history",
        amount: Number(balanceInfo.currentBalance || 0)
      }],
      expenseAmount: 0,
      allowanceAmount: 0,
      expenseGroupId: "-",
      computedBalance: Number(balanceInfo.currentBalance || 0)
    }];
  }, [balanceInfo.currentBalance, balanceInfo.lastClearedAt, statementRows, viewTarget, selectedEngineer, selectedAccount]);

  const filteredVouchers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    const dateFiltered = visibleStatementRows.filter((voucher) => {
      const dateValue = String(voucher.date || "").slice(0, 10);
      if (fromDate && dateValue < fromDate) return false;
      if (toDate && dateValue > toDate) return false;
      return true;
    });

    const searched = query
      ? dateFiltered.filter((voucher) => {
          const expenseLines = voucher.details
            .map((row) => [row.title, row.meta].filter(Boolean).join(" - "))
            .join(" ");
          return [
            formatDisplayDate(voucher.date),
            voucher.expenseGroupId,
            voucher.complaintGeneral,
            voucher.refText,
            expenseLines,
            voucher.allowanceAmount,
            voucher.expenseAmount,
            voucher.computedBalance,
          ].join(" ").toLowerCase().includes(query);
        })
      : dateFiltered;

    return [...searched].sort((first, second) => {
      const firstValue = sortValue(first, sortConfig.field);
      const secondValue = sortValue(second, sortConfig.field);
      const direction = sortConfig.direction === "asc" ? 1 : -1;

      if (firstValue > secondValue) return direction;
      if (firstValue < secondValue) return -direction;
      return 0;
    });
  }, [fromDate, searchTerm, sortConfig, toDate, visibleStatementRows]);

  const displayedVouchers = useMemo(() => {
    return filteredVouchers.slice(0, vouchersLimit);
  }, [filteredVouchers, vouchersLimit]);

  const totals = useMemo(() => {
    const amount = filteredVouchers.reduce((sum, voucher) => sum + Number(voucher.expenseAmount || 0), 0);
    const allowanceAmount = filteredVouchers.reduce((sum, voucher) => sum + Number(voucher.allowanceAmount || 0), 0);

    return {
      allowanceAmount,
      amount,
      balance: Number(balanceInfo.currentBalance || 0)
    };
  }, [balanceInfo.currentBalance, filteredVouchers]);

  const activeAllowanceTotal = Number(balanceInfo.currentBalance || 0);
  const activeSpent = useMemo(() => 0, []);

  const updateAllowanceForm = (field, value) => {
    setAllowanceForm((current) => ({ ...current, [field]: value }));
  };

  const saveAllowance = async () => {
    const amount = Number(allowanceForm.amount);
    const hasTarget = viewTarget === "engineer" ? selectedEngineerDetails.engineerId : selectedAccountDetails.email;

    if (!hasTarget) {
      alert(`Please choose ${viewTarget === "engineer" ? "an engineer" : "an accountant"} first`);
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      alert("Please enter a valid credit amount");
      return;
    }

    try {
      setSavingAllowance(true);

      const payload = {
        ownerRole: viewTarget,
        entryType: "non-complaint",
        location: allowanceForm.location || `${viewTarget === "engineer" ? "Engineer" : "Accounts"} credit`,
        allowanceDate: allowanceForm.allowanceDate,
        amount,
        givenByRole: "admin",
        givenByName: adminName,
        notes: allowanceForm.notes
      };

      if (viewTarget === "engineer") {
        payload.engineerId = selectedEngineerDetails.engineerId;
        payload.engineerName = selectedEngineerDetails.name || selectedEngineerDetails.engineerId;
      } else {
        payload.accountId = selectedAccountDetails.email || selectedAccountDetails._id;
        payload.accountName = selectedAccountDetails.name || selectedAccountDetails.email;
      }

      await axios.post(API_BASE_URL + "/api/vouchers/allowances", payload);
      setAllowanceForm({
        location: "",
        amount: "",
        allowanceDate: todayInputValue(),
        notes: ""
      });
      setShowAllowanceForm(false);
      await fetchPageData();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save credit");
    } finally {
      setSavingAllowance(false);
    }
  };

  const clearVouchersForDate = async () => {
    if (clearDateConfirmationText !== "YES") return;
    const hasTarget = viewTarget === "engineer" ? selectedEngineerDetails.engineerId : selectedAccountDetails.email;

    if (!hasTarget) {
      alert(`Please choose ${viewTarget === "engineer" ? "an engineer" : "an accountant"} first`);
      return;
    }

    try {
      setClearingVoucher(true);
      const payload = {
        voucherDate: clearVoucherDate,
        ownerRole: viewTarget,
        requestedByRole: "admin",
        createdByUserId: adminName
      };

      if (viewTarget === "engineer") {
        payload.engineerId = selectedEngineerDetails.engineerId;
        payload.engineerName = selectedEngineerDetails.name || selectedEngineerDetails.engineerId;
      } else {
        payload.accountId = selectedAccountDetails.email || selectedAccountDetails._id;
        payload.accountName = selectedAccountDetails.name || selectedAccountDetails.email;
      }

      const response = await axios.post(API_BASE_URL + "/api/vouchers/clear-by-date", payload);

      alert(response.data?.message || "Voucher cleared successfully");
      setShowClearDateModal(false);
      setClearDateConfirmationText("");
      await fetchPageData();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to clear vouchers");
    } finally {
      setClearingVoucher(false);
    }
  };

  const clearAllVouchers = async () => {
    if (clearAllConfirmationText !== "YES") return;
    const hasTarget = viewTarget === "engineer" ? selectedEngineerDetails.engineerId : selectedAccountDetails.email;

    if (!hasTarget) {
      alert(`Please choose ${viewTarget === "engineer" ? "an engineer" : "an accountant"} first`);
      return;
    }

    try {
      setClearingAllVouchers(true);
      const payload = {
        ownerRole: viewTarget,
        requestedByRole: "admin",
        createdByUserId: adminName
      };

      if (viewTarget === "engineer") {
        payload.engineerId = selectedEngineerDetails.engineerId;
        payload.engineerName = selectedEngineerDetails.name || selectedEngineerDetails.engineerId;
      } else {
        payload.accountId = selectedAccountDetails.email || selectedAccountDetails._id;
        payload.accountName = selectedAccountDetails.name || selectedAccountDetails.email;
      }

      const response = await axios.post(API_BASE_URL + "/api/vouchers/clear-all", payload);

      alert(response.data?.message || "All vouchers cleared successfully");
      setShowClearAllModal(false);
      setClearAllConfirmationText("");
      await fetchPageData();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to clear all vouchers");
    } finally {
      setClearingAllVouchers(false);
    }
  };

  const downloadVoucherPdf = () => {
    const hasTarget = viewTarget === "engineer" ? selectedEngineerDetails.engineerId : selectedAccountDetails.email;
    if (!hasTarget) {
      alert(`Please choose ${viewTarget === "engineer" ? "an engineer" : "an accountant"} first`);
      return;
    }

    const params = new URLSearchParams({
      ownerRole: viewTarget,
      download: "1"
    });

    if (viewTarget === "engineer") {
      params.set("engineer", selectedEngineerDetails.engineerId);
      params.set("engineerId", selectedEngineerDetails.engineerId);
    } else {
      params.set("account", selectedAccountDetails.email);
      params.set("accountId", selectedAccountDetails.email);
    }

    if (fromDate) params.set("fromDate", fromDate);
    if (toDate) params.set("toDate", toDate);

    window.open(appendAuthToken(API_BASE_URL + `/api/vouchers/statement/pdf?${params.toString()}`), "_blank", "noopener,noreferrer");
  };

  const clearVoucherPreview = useMemo(() => {
    const voucherGroups = statementRows.filter(
      (voucher) => String(voucher.date || "").slice(0, 10) === clearVoucherDate
    );

    return {
      voucherCount: voucherGroups.length,
      totalAmount: voucherGroups.reduce(
        (sum, voucher) => sum + Number(voucher.expenseAmount || 0) + Number(voucher.allowanceAmount || 0),
        0
      )
    };
  }, [clearVoucherDate, statementRows]);

  const clearAllPreview = useMemo(() => ({
    voucherCount: groupedVouchers.length + allowances.length,
    expenseTotal: groupedVouchers.reduce((sum, voucher) => sum + Number(voucher.totalAmount || 0), 0),
    allowanceTotal: allowances.reduce((sum, allowance) => sum + Number(allowance.amount || 0), 0),
    preservedBalance: Number(balanceInfo.currentBalance || 0)
  }), [allowances, balanceInfo.currentBalance, groupedVouchers]);

  return (
    <div className="space-y-4 p-2 text-slate-800">
      {/* Clean, modern header container */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        {/* Top Title & Actions Row */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-md">
              Admin: {adminName}
            </span>
            <h1 className="text-xl font-bold text-slate-900 mt-1.5">
              {viewTarget === "engineer" ? "Engineer Voucher Control" : "Accounts Voucher Control"}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowAllowanceForm((current) => !current)}
              className="rounded-xl bg-slate-900 hover:bg-slate-800 px-4 py-2 text-xs font-semibold text-white shadow-sm transition"
            >
              {showAllowanceForm ? "Hide Credit Form" : "+ Add Credit"}
            </button>
            <button
              type="button"
              onClick={() => {
                setClearAllConfirmationText("");
                setShowClearAllModal(true);
              }}
              className="rounded-xl border border-rose-300 bg-white hover:bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-700 transition"
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Subtle Horizontal Divider */}
        <div className="border-t border-slate-100 my-4" />

        {/* Filter Inputs Row (Balanced 4 Column Grid) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <SelectField label="Voucher Type" value={viewTarget} onChange={(event) => {
              setViewTarget(event.target.value);
              setShowAllowanceForm(false);
            }}>
              <option value="engineer">Engineers</option>
              <option value="accounts">Accounts Personnel</option>
            </SelectField>
          </div>
          <div>
            {viewTarget === "engineer" ? (
              <SelectField label="Engineer" value={selectedEngineer} onChange={(event) => setSelectedEngineer(event.target.value)}>
                <option value="">Select engineer</option>
                {engineers.map((engineer) => (
                  <option key={engineer._id} value={engineer._id}>{engineer.name || engineer.engineerId}</option>
                ))}
              </SelectField>
            ) : (
              <SelectField label="Accountant" value={selectedAccount} onChange={(event) => setSelectedAccount(event.target.value)}>
                <option value="">Select accountant</option>
                {accountsList.map((account) => (
                  <option key={account._id} value={account._id}>{account.name || account.email}</option>
                ))}
              </SelectField>
            )}
          </div>
          <div>
            <InputField label="From Date" type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
          </div>
          <div>
            <InputField label="To Date" type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
          </div>
        </div>
      </section>

      {showAllowanceForm && (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <h2 className="text-sm font-bold text-slate-900">
              Add {viewTarget === "engineer" ? "Engineer" : "Accountant"} Credit
            </h2>
            <div className="rounded-lg bg-emerald-50 px-2.5 py-1 text-xs ring-1 ring-emerald-100">
              <span className="font-semibold text-slate-600">Balance: </span>
              <span className="font-bold text-emerald-700">{formatCurrency(activeAllowanceTotal - activeSpent)}</span>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 text-xs">
            <InputField label="Credit Date" type="date" value={allowanceForm.allowanceDate} onChange={(event) => updateAllowanceForm("allowanceDate", event.target.value)} />
            <InputField label="Credit Amount" type="number" min="0" step="0.01" value={allowanceForm.amount} onChange={(event) => updateAllowanceForm("amount", event.target.value)} placeholder="0.00" />
            <InputField
              label="Location / Purpose"
              value={allowanceForm.location}
              onChange={(event) => updateAllowanceForm("location", event.target.value)}
              placeholder={viewTarget === "engineer" ? "Office, site visit..." : "Office credit..."}
            />
            <div className="sm:col-span-3">
              <InputField label="Notes" value={allowanceForm.notes} onChange={(event) => updateAllowanceForm("notes", event.target.value)} placeholder="Optional description..." />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={saveAllowance}
              disabled={savingAllowance}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:bg-slate-400"
            >
              {savingAllowance ? "Saving..." : "Save Credit"}
            </button>
          </div>
        </section>
      )}

      {/* Main ledger table block */}
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between border-b border-slate-100 pb-3">
          <div>
            <h2 className="text-sm font-bold text-slate-900">
              {viewTarget === "engineer"
                ? (selectedEngineerDetails.name ? `${selectedEngineerDetails.name} Voucher Ledger` : "Voucher Ledger")
                : (selectedAccountDetails.name ? `${selectedAccountDetails.name} Voucher Ledger` : "Voucher Ledger")
              }
            </h2>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="grid grid-cols-4 gap-1.5 text-[11px]">
              <SummaryPill label="Opening" value={formatCurrency(balanceInfo.openingBalance)} theme="blue" />
              <SummaryPill label="Credit" value={formatCurrency(totals.allowanceAmount)} theme="emerald" />
              <SummaryPill label="Debit" value={formatCurrency(totals.amount)} theme="rose" />
              <SummaryPill label="Balance" value={formatCurrency(balanceInfo.currentBalance)} theme={balanceInfo.currentBalance < 0 ? "rose" : "emerald"} />
            </div>
            <button
              type="button"
              onClick={downloadVoucherPdf}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 mt-2 sm:mt-0"
            >
              PDF
            </button>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-12 gap-3 text-xs">
          <div className="col-span-12 md:col-span-6">
            <InputField label="Search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search remarks..." />
          </div>
          <div className="col-span-6 md:col-span-3">
            <SelectField label="Sort By" value={sortConfig.field} onChange={(event) => setSortConfig((current) => ({ ...current, field: event.target.value }))}>
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </SelectField>
          </div>
          <div className="col-span-6 md:col-span-3">
            <SelectField label="Order" value={sortConfig.direction} onChange={(event) => setSortConfig((current) => ({ ...current, direction: event.target.value }))}>
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </SelectField>
          </div>
        </div>

        {!(viewTarget === "engineer" ? selectedEngineer : selectedAccount) ? (
          <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 py-8 text-center text-xs text-slate-400">
            Select {viewTarget === "engineer" ? "an engineer" : "an accountant"} to view admin voucher tracking.
          </div>
        ) : loading ? (
          <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 py-8 text-center text-xs text-slate-400">
            Loading vouchers...
          </div>
        ) : filteredVouchers.length === 0 ? (
          <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 py-8 text-center text-xs text-slate-400">
            No vouchers found. Opening balance remains {formatCurrency(balanceInfo.currentBalance)}.
          </div>
        ) : (
          <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-xs">
                <thead className="bg-slate-50 text-left text-slate-500 uppercase tracking-wider font-semibold border-b border-slate-200 text-[10px]">
                  <tr>
                    <th className="px-3 py-2 whitespace-nowrap">Date / ID</th>
                    <th className="px-3 py-2 whitespace-nowrap">Category / Ref</th>
                    <th className="px-3 py-2">Debit Details</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Debit</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Credit</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Balance</th>
                    <th className="px-3 py-2 text-center whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700 bg-white">
                  {displayedVouchers.map((voucher) => {
                    return (
                      <tr key={voucher._id} className="align-top transition hover:bg-slate-50/50">
                        {/* Date / ID */}
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="font-semibold text-slate-900">{formatDisplayDate(voucher.date)}</div>
                          <div className="mt-0.5 text-[9px] text-slate-400 font-mono tracking-tight" title={voucher.expenseGroupId}>
                            {voucher.expenseGroupId !== "-" ? `ID: ${String(voucher.expenseGroupId).substring(0, 10)}...` : "Allowance"}
                          </div>
                        </td>

                        {/* Category / Ref */}
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-bold ${voucher.badgeTone === "sky" ? "bg-sky-50 text-sky-700 ring-1 ring-sky-600/10" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/10"}`}>
                            {voucher.complaintGeneral.replace(" Expense", "")}
                          </span>
                          {voucher.refText !== "-" && (
                            <div className="mt-1 text-[10px] text-slate-400 truncate max-w-[150px]" title={voucher.refText}>
                              {voucher.refText}
                            </div>
                          )}
                        </td>

                        {/* Debit Details */}
                        <td className="px-3 py-2 text-slate-700">
                          <div className="space-y-1 max-w-[400px]">
                            {voucher.details.map((row, index) => (
                              <div key={index} className="leading-relaxed">
                                <span className="font-bold text-slate-800">{row.title}</span>
                                {row.meta ? (
                                  <span className="text-slate-500 font-normal ml-1">({row.meta})</span>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </td>

                        {/* Debit */}
                        <td className="px-3 py-2 text-right font-bold text-rose-600 whitespace-nowrap">
                          {voucher.kind === "expense" ? `- ${formatCurrency(voucher.expenseAmount)}` : ""}
                        </td>

                        {/* Credit */}
                        <td className="px-3 py-2 text-right font-bold text-emerald-600 whitespace-nowrap">
                          {voucher.kind === "allowance" ? `+ ${formatCurrency(voucher.allowanceAmount)}` : ""}
                        </td>

                        {/* Balance */}
                        <td className={`px-3 py-2 text-right font-bold whitespace-nowrap ${voucher.computedBalance < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                          {formatCurrency(voucher.computedBalance)}
                        </td>

                        {/* Action */}
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => {
                              const targetId = voucher.expenseGroupId && voucher.expenseGroupId !== "-" ? voucher.expenseGroupId : voucher._id;
                              if (targetId) {
                                window.open(appendAuthToken(API_BASE_URL + `/api/vouchers/${targetId}/pdf?download=1`), "_blank", "noopener,noreferrer");
                              }
                            }}
                            className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 transition hover:bg-emerald-100"
                            title="Download Voucher Slip PDF"
                          >
                            PDF
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination / View More for Vouchers */}
            {filteredVouchers.length > vouchersLimit && (
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                <div className="text-xs font-semibold text-slate-500">
                  Showing <span className="font-bold text-slate-800">{displayedVouchers.length}</span> of <span className="font-bold text-slate-800">{filteredVouchers.length}</span> entries
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setVouchersLimit((prev) => prev + 20)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-xs transition"
                  >
                    <span className="material-symbols-outlined text-sm font-bold">expand_more</span>
                    View More (+20)
                  </button>
                  <button
                    type="button"
                    onClick={() => setVouchersLimit(filteredVouchers.length)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold transition"
                  >
                    View All ({filteredVouchers.length})
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {showClearDateModal && (
        <ConfirmationModal
          title="Clear Selected Date"
          clearVoucherDate={clearVoucherDate}
          setClearVoucherDate={setClearVoucherDate}
          clearVoucherPreview={clearVoucherPreview}
          clearConfirmationText={clearDateConfirmationText}
          setClearConfirmationText={setClearDateConfirmationText}
          clearingVoucher={clearingVoucher}
          onClose={() => {
            if (clearingVoucher) return;
            setShowClearDateModal(false);
            setClearDateConfirmationText("");
          }}
          onConfirm={clearVouchersForDate}
          confirmLabel="Clear"
        />
      )}

      {showClearAllModal && (
        <ClearAllConfirmationModal
          clearAllPreview={clearAllPreview}
          clearConfirmationText={clearAllConfirmationText}
          setClearConfirmationText={setClearAllConfirmationText}
          clearingVoucher={clearingAllVouchers}
          onClose={() => {
            if (clearingAllVouchers) return;
            setShowClearAllModal(false);
            setClearAllConfirmationText("");
          }}
          onConfirm={clearAllVouchers}
        />
      )}
    </div>
  );
}

function ConfirmationModal({
  title,
  clearVoucherDate,
  setClearVoucherDate,
  clearVoucherPreview,
  clearConfirmationText,
  setClearConfirmationText,
  clearingVoucher,
  onClose,
  onConfirm,
  confirmLabel
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl border border-slate-100 text-xs">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-3">
          <div>
            <h3 className="text-sm font-bold text-slate-900">{title}</h3>
            <p className="mt-1 text-[11px] text-slate-400">Only vouchers for the selected date will be cleared.</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 font-bold">×</button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Target Date</span>
            <input
              type="date"
              value={clearVoucherDate}
              onChange={(event) => setClearVoucherDate(event.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
            />
          </label>
          <div className="rounded-lg border border-rose-100 bg-rose-50/50 p-2.5 text-[11px] text-rose-800">
            <p><span className="font-semibold">Vouchers:</span> {clearVoucherPreview.voucherCount}</p>
            <p className="mt-1"><span className="font-semibold">Amount:</span> {formatCurrency(clearVoucherPreview.totalAmount)}</p>
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <label className="block">
            <span className="mb-1.5 block font-semibold text-slate-600">Type YES to confirm:</span>
            <input
              value={clearConfirmationText}
              onChange={(event) => setClearConfirmationText(event.target.value)}
              placeholder="YES"
              className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 outline-none focus:border-rose-400"
            />
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-1.5 text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={clearConfirmationText !== "YES" || clearVoucherPreview.voucherCount === 0 || clearingVoucher}
            className="rounded-lg bg-rose-600 px-3 py-1.5 font-semibold text-white transition hover:bg-rose-700 disabled:bg-slate-200 disabled:text-slate-400"
          >
            {clearingVoucher ? "Clearing..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClearAllConfirmationModal({
  clearAllPreview,
  clearConfirmationText,
  setClearConfirmationText,
  clearingVoucher,
  onClose,
  onConfirm
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl border border-slate-100 text-xs">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-3">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Clear All Vouchers</h3>
            <p className="mt-1 text-[11px] text-slate-400">All historical details will be wiped. Current balance remains preserved.</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 font-bold">×</button>
        </div>

        <div className="mt-4 rounded-lg border border-rose-100 bg-rose-50/50 p-3 text-[11px] text-rose-800 space-y-1">
          <p><span className="font-semibold">Voucher Count:</span> {clearAllPreview.voucherCount}</p>
          <p><span className="font-semibold">Debit Total:</span> {formatCurrency(clearAllPreview.expenseTotal)}</p>
          <p><span className="font-semibold">Credit Total:</span> {formatCurrency(clearAllPreview.allowanceTotal)}</p>
          <p><span className="font-semibold">Preserved Balance:</span> {formatCurrency(clearAllPreview.preservedBalance)}</p>
        </div>

        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <label className="block">
            <span className="mb-1.5 block font-semibold text-slate-600">Type YES to confirm:</span>
            <input
              value={clearConfirmationText}
              onChange={(event) => setClearConfirmationText(event.target.value)}
              placeholder="YES"
              className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 outline-none focus:border-rose-400"
            />
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-1.5 text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={clearConfirmationText !== "YES" || clearAllPreview.voucherCount === 0 || clearingVoucher}
            className="rounded-lg bg-rose-600 px-3 py-1.5 font-semibold text-white transition hover:bg-rose-700 disabled:bg-slate-200 disabled:text-slate-400"
          >
            {clearingVoucher ? "Clearing..." : "Clear All"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InputField({ label, ...props }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
      <input {...props} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" />
    </label>
  );
}

function SelectField({ label, children, ...props }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
      <select {...props} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100">
        {children}
      </select>
    </label>
  );
}

function SummaryPill({ label, value, theme = "emerald" }) {
  const themeClasses = theme === "rose" 
    ? "bg-rose-50 text-rose-800 ring-rose-100" 
    : theme === "blue"
    ? "bg-blue-50 text-blue-800 ring-blue-100"
    : "bg-emerald-50 text-emerald-800 ring-emerald-100";
  return (
    <div className={`rounded-lg px-2.5 py-1 text-center shadow-sm ring-1 ${themeClasses}`}>
      <span className="text-[9px] font-bold uppercase tracking-wider opacity-75">{label}</span>
      <p className="font-extrabold text-[11px] mt-0.5">{value}</p>
    </div>
  );
}

export default function AdminVouchers() {
  const [useRedesigned, setUseRedesigned] = useState(true);

  if (useRedesigned) {
    return (
      <div className="space-y-4">
        <VoucherSimpleManager />
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-7xl mx-auto p-4 mb-2 bg-indigo-50 border border-indigo-200 rounded-xl flex items-center justify-between">
        <span className="text-xs font-bold text-indigo-800">You are currently viewing the legacy voucher view.</span>
        <button
          onClick={() => setUseRedesigned(true)}
          className="px-3 py-1 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-xs"
        >
          Open Redesigned Financial System
        </button>
      </div>
      <LegacyAdminVouchers />
    </div>
  );
}
